import React, { useCallback, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';

import { getTests, getCenters } from '@dyl/apiClients/backendApi';
import createDatabaseManager from '@dyl/utils/createDatabaseManager';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useLevels } from "@dyl/systems/LevelsManager";

const { Provider: TestsManager, use: useRaw } = createDatabaseManager({
    requestHook: function useTestsRequest(setLoading) {
        const { enqueueSnackbar } = useSnackbar();
        return useCallback(async(args) => {
            const { level } = args;
            try {
                setLoading?.(true);
                const tests = await getTests(level.level_id);
                const centres = await getCenters(level.level_id);
                setLoading?.(false);
                return { tests, centres };
            }
            catch (error) {
                await new Promise(resolve => enqueueSnackbar('Failed to load tests', {
                    variant: 'error',
                    action: createSnackbarAction({
                        iconComponent: RefreshIcon,
                        onClick: resolve
                    }),
                    persist: true,
                }));
            }
        });
    },
    defaultValue: { tests: [], centres: [] },
    contextName: 'TestsManager'
});

export default TestsManager;

export function useTests() {
    const { globalLevel, levelsStatus } = useLevels();
    const {
        data: { tests = [] },
        refresh: refreshTests,
        status: testsStatus,
    } = useRaw();

    useEffect(() => {
        // FIXME: Still triggers twice iff rubric/component response arrives before testGroup response.
        if (levelsStatus !== 'complete' || testsStatus === 'loading') return;
        refreshTests({ level: globalLevel });
    }, [globalLevel]);

    return { tests, refreshTests, testsStatus };
}

export function useCentres() {
    const { globalLevel, levelsStatus } = useLevels();
    const {
        data: { centres = [] },
        refresh: refreshTests,
    } = useRaw();

    useEffect(() => {
        if (levelsStatus !== 'complete') return;
        refreshTests({ level: globalLevel });
    }, [globalLevel]);
    return { centres, refreshTests, centreStatus: 'complete' };
}

export function useLegacy() {
    const { globalLevel, levelsStatus } = useLevels();
    const {
        data: { centres = [] },
        refresh: refreshTests,
    } = useRaw();

    useEffect(() => {
        if (levelsStatus !== 'complete') return;
        refreshTests({ level: globalLevel });
    }, [globalLevel]);
    return { centres, refreshTests, legacyStatus: 'complete' };
}

/**
 * Renders the 'name' (or 'readable ID') of a test, as entered by an admin user.
 *
 * If the tests list has not been loaded, a progress indicator is rendered.
 */
export function TestName({ testId }) {
    const { tests, testsStatus } = useTests();

    const name = useMemo(() => {
        const test = tests.find(t => t.test_group_id === testId);
        return test?.name ?? '???';
    }, [tests, testId]);

    return (
        testsStatus !== 'complete' ? <CircularProgress size='1em' />
        : name
    );
}